// Account list + detail + mutation hooks — FE-038 / FE-039 / FE-040.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  addAccountTeamMember,
  createAccount,
  createAccountContact,
  createAccountTag,
  deleteAccountContact,
  fetchAccountContacts,
  fetchAccountDetail,
  fetchAccountList,
  fetchAccountTags,
  fetchAccountTeam,
  fetchBrokerLocationsForAccounts,
  fetchNaicsSectors,
  patchAccountTeamMember,
  removeAccountTeamMember,
  searchNaicsCodes,
  setPrimaryAccountContact,
  updateAccount,
  updateAccountContact,
} from "./api";
import type {
  AccountContact,
  AccountContactInput,
  AccountContactListResponse,
  AccountContactPatch,
  AccountDetail,
  AccountListQuery,
  AccountListResponse,
  AccountMutationResponse,
  AccountTagSummary,
  AccountTeamListResponse,
  AccountTeamMember,
  AddAccountTeamMemberInput,
  CreateAccountInput,
  NaicsSearchResult,
  NaicsSectorOption,
  UpdateAccountInput,
} from "./types";
import { AccountNotFoundError } from "./types";


export const ACCOUNTS_QUERY_KEY = "accounts" as const;


export function useAccountListQuery(
  query: AccountListQuery,
): UseQueryResult<AccountListResponse> {
  return useQuery({
    queryKey: [ACCOUNTS_QUERY_KEY, "list", query],
    queryFn: ({ signal }) => fetchAccountList(query, { signal }),
  });
}


export function useAccountDetailQuery(
  accountId: string | undefined,
): UseQueryResult<AccountDetail> {
  return useQuery({
    queryKey: [ACCOUNTS_QUERY_KEY, "detail", accountId],
    queryFn: ({ signal }) => fetchAccountDetail(accountId!, { signal }),
    enabled: Boolean(accountId),
    // Don't retry the explicit not-found case — a typo in the URL
    // shouldn't burn extra round-trips before the page renders the
    // 404 view.
    retry: (failureCount, error) =>
      !(error instanceof AccountNotFoundError) && failureCount < 1,
  });
}


export function useCreateAccountMutation(): UseMutationResult<
  AccountMutationResponse,
  Error,
  CreateAccountInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAccountInput) => createAccount(input),
    onSuccess: () => {
      // Bust any cached list query so the new Account appears
      // when the user navigates back from the detail page.
      queryClient.invalidateQueries({
        queryKey: [ACCOUNTS_QUERY_KEY, "list"],
      });
    },
  });
}


export interface UpdateAccountVariables {
  account_id: string;
  values: UpdateAccountInput;
}


export function useUpdateAccountMutation(): UseMutationResult<
  AccountMutationResponse,
  Error,
  UpdateAccountVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: UpdateAccountVariables) =>
      updateAccount(vars.account_id, vars.values),
    onSuccess: (_data, vars) => {
      // Refresh the affected detail row + the list.
      queryClient.invalidateQueries({
        queryKey: [ACCOUNTS_QUERY_KEY, "detail", vars.account_id],
      });
      queryClient.invalidateQueries({
        queryKey: [ACCOUNTS_QUERY_KEY, "list"],
      });
    },
  });
}


// ── Accounts-01 — NAICS reference + tag inventory hooks ──────────────────


export const NAICS_SECTORS_QUERY_KEY = "naics-sectors" as const;
export const NAICS_SEARCH_QUERY_KEY = "naics-search" as const;
export const ACCOUNT_TAGS_QUERY_KEY = "account-tags" as const;
export const BROKER_LOCATIONS_FOR_ACCOUNTS_QUERY_KEY =
  "broker-locations-for-accounts" as const;


/** Static list of 20 NAICS sectors plus ``unknown``. Cached for the
 * session — the data never changes mid-session. */
export function useNaicsSectorsQuery(): UseQueryResult<{
  sectors: NaicsSectorOption[];
}> {
  return useQuery({
    queryKey: [NAICS_SECTORS_QUERY_KEY],
    queryFn: ({ signal }) => fetchNaicsSectors({ signal }),
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
}


/** 6-digit NAICS typeahead. Caller is responsible for debouncing the
 * input on the FE side; the hook simply caches by query key. Returns
 * an empty result when ``q`` is shorter than 2 chars. */
export function useNaicsSearchQuery(
  params: { q: string; sector?: string; limit?: number },
): UseQueryResult<{ results: NaicsSearchResult[] }> {
  const q = (params.q || "").trim();
  const enabled = q.length >= 2;
  return useQuery({
    queryKey: [NAICS_SEARCH_QUERY_KEY, q, params.sector ?? "", params.limit ?? 10],
    queryFn: ({ signal }) => searchNaicsCodes(params, { signal }),
    enabled,
    staleTime: 30 * 1000,
  });
}


export function useAccountTagsQuery(): UseQueryResult<{
  rows: AccountTagSummary[]; total: number;
}> {
  return useQuery({
    queryKey: [ACCOUNT_TAGS_QUERY_KEY],
    queryFn: ({ signal }) => fetchAccountTags({ signal }),
  });
}


export function useCreateAccountTagMutation(): UseMutationResult<
  AccountTagSummary, Error, string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createAccountTag(name),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [ACCOUNT_TAGS_QUERY_KEY] });
    },
  });
}


// ── Accounts-Teams-01 — Client team hooks ────────────────────────────────


export const ACCOUNT_TEAM_QUERY_KEY = "account-team" as const;


export function useAccountTeamQuery(
  accountId: string | undefined,
): UseQueryResult<AccountTeamListResponse> {
  return useQuery({
    queryKey: [ACCOUNT_TEAM_QUERY_KEY, accountId],
    queryFn: ({ signal }) => fetchAccountTeam(accountId!, { signal }),
    enabled: Boolean(accountId),
  });
}


function _invalidateAccountTeam(
  qc: ReturnType<typeof useQueryClient>, accountId: string,
) {
  qc.invalidateQueries({
    queryKey: [ACCOUNT_TEAM_QUERY_KEY, accountId],
  });
  qc.invalidateQueries({
    queryKey: [ACCOUNTS_QUERY_KEY, "detail", accountId],
  });
}


export function useAddAccountTeamMemberMutation(
  accountId: string,
): UseMutationResult<AccountTeamMember, Error, AddAccountTeamMemberInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddAccountTeamMemberInput) =>
      addAccountTeamMember(accountId, input),
    onSuccess: () => _invalidateAccountTeam(qc, accountId),
  });
}


export interface PatchAccountTeamVariables {
  assignment_id: string;
  values: { is_account_lead?: boolean };
}


export function usePatchAccountTeamMemberMutation(
  accountId: string,
): UseMutationResult<AccountTeamMember, Error, PatchAccountTeamVariables> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: PatchAccountTeamVariables) =>
      patchAccountTeamMember(accountId, vars.assignment_id, vars.values),
    onSuccess: () => _invalidateAccountTeam(qc, accountId),
  });
}


export function useRemoveAccountTeamMemberMutation(
  accountId: string,
): UseMutationResult<
  { id: string; removed: boolean }, Error, string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assignmentId: string) =>
      removeAccountTeamMember(accountId, assignmentId),
    onSuccess: () => _invalidateAccountTeam(qc, accountId),
  });
}


// ── Accounts-Contacts-01 — Account contacts hooks ────────────────────────


export const ACCOUNT_CONTACTS_QUERY_KEY = "account-contacts" as const;


export function useAccountContactsQuery(
  accountId: string | undefined,
): UseQueryResult<AccountContactListResponse> {
  return useQuery({
    queryKey: [ACCOUNT_CONTACTS_QUERY_KEY, accountId],
    queryFn: ({ signal }) => fetchAccountContacts(accountId!, { signal }),
    enabled: Boolean(accountId),
  });
}


function _invalidateAccountContacts(
  qc: ReturnType<typeof useQueryClient>, accountId: string,
) {
  qc.invalidateQueries({
    queryKey: [ACCOUNT_CONTACTS_QUERY_KEY, accountId],
  });
  // Account detail embeds the primary contact summary, so refresh
  // it too whenever the contact set changes.
  qc.invalidateQueries({
    queryKey: [ACCOUNTS_QUERY_KEY, "detail", accountId],
  });
}


export function useCreateAccountContactMutation(
  accountId: string,
): UseMutationResult<AccountContact, Error, AccountContactInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AccountContactInput) =>
      createAccountContact(accountId, input),
    onSuccess: () => _invalidateAccountContacts(qc, accountId),
  });
}


export interface UpdateAccountContactVariables {
  contact_id: string;
  values: AccountContactPatch;
}


export function useUpdateAccountContactMutation(
  accountId: string,
): UseMutationResult<AccountContact, Error, UpdateAccountContactVariables> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: UpdateAccountContactVariables) =>
      updateAccountContact(accountId, vars.contact_id, vars.values),
    onSuccess: () => _invalidateAccountContacts(qc, accountId),
  });
}


export function useDeleteAccountContactMutation(
  accountId: string,
): UseMutationResult<{ id: string; deleted: boolean }, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (contactId: string) =>
      deleteAccountContact(accountId, contactId),
    onSuccess: () => _invalidateAccountContacts(qc, accountId),
  });
}


export function useSetPrimaryAccountContactMutation(
  accountId: string,
): UseMutationResult<AccountContact, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (contactId: string) =>
      setPrimaryAccountContact(accountId, contactId),
    onSuccess: () => _invalidateAccountContacts(qc, accountId),
  });
}


export function useBrokerLocationsForAccountsQuery(): UseQueryResult<{
  rows: Array<{
    location_id: string;
    name: string;
    city: string | null;
    state_code: string | null;
    status: "active" | "inactive";
  }>;
}> {
  return useQuery({
    queryKey: [BROKER_LOCATIONS_FOR_ACCOUNTS_QUERY_KEY],
    queryFn: ({ signal }) =>
      fetchBrokerLocationsForAccounts({ signal }),
    staleTime: 60 * 1000,
  });
}
